import Page from "components/Page"
import MediaCard from "components/MediaCard"
import { getMediaEpisodesBySection, MEDIA_EPISODES, MEDIA_SHOWS, MediaSection } from "data/media"
import usePageMeta from "hooks/usePageMeta"
import { getYoutubeThumbnailUrl } from "utils/socialPreview"

const sectionCopy: Record<MediaSection, string> = {
  "studio-visit": "Long-form studio conversations with artists, collectors, photographers, and creative technologists.",
  "beyond-the-blocks": "Video conversations with Art Blocks about releases, physical practice, displays, and generative culture.",
  "desert-dialogues": "Marfa conversations recorded during Art Blocks Weekend, collected as their own video series.",
}

const sectionOrder: MediaSection[] = ["studio-visit", "beyond-the-blocks", "desert-dialogues"]

const MediaPage = () => {
  usePageMeta(
    "Media — r4v3n",
    "Podcasts, interviews, and video conversations hosted by r4v3n, spanning artist studios, Art Blocks releases, generative systems, collecting, and digital culture.",
    getYoutubeThumbnailUrl(MEDIA_EPISODES[0]?.youtubeId),
    "/media",
  )
  return (
    <Page>
      <div className="gallery-page-head">
        <div className="max-w-3xl">
          <h1 className="gallery-heading">Media</h1>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-gallery-muted">
            Podcasts, interviews, and video conversations hosted by r4v3n, spanning artist studios,
            Art Blocks releases, generative systems, collecting, and digital culture.
          </p>
        </div>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-14 md:mb-16">
        {MEDIA_SHOWS.map((show) => (
          <div key={show.id} className="border border-gallery-border bg-gallery-surface p-5 md:p-6">
            <p className="gallery-label text-gallery-muted">{show.shortTitle}</p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl leading-tight font-normal">
              {show.title}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-gallery-muted">
              {show.description}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {show.links.map((link) => (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gallery-btn text-xs px-4 py-2"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        ))}
      </section>

      <div className="space-y-16 md:space-y-20">
        {sectionOrder.map((section) => {
          const episodes = getMediaEpisodesBySection(section)
          const gridClass = section === "desert-dialogues"
            ? "grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
            : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"

          return (
            <section key={section}>
              <div className="mb-6 md:mb-8 max-w-3xl">
                <p className="gallery-label text-gallery-muted">{episodes.length} episodes</p>
                <h2 className="gallery-subheading mt-2">
                  {episodes[0]?.seriesTitle}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-gallery-muted">
                  {sectionCopy[section]}
                </p>
              </div>

              <div className={gridClass}>
                {episodes.map((episode) => (
                  <MediaCard
                    key={episode.slug}
                    episode={episode}
                    compact={section === "desert-dialogues"}
                  />
                ))}
              </div>
            </section>
          )
        })}
      </div>
    </Page>
  )
}

export default MediaPage
